import api from './api';
import { User } from '../types';
export type { User };

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  token_type: string;
  refresh_token: string;
  expires_in: number;
  user: User;
}

/**
 * Authenticate a user and get a JWT token
 */
export const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
  const response = await api.post<LoginResponse>('/api/v1/auth/login', credentials);
  return response.data;
};

export const samlLogin = (): void => {
  const frontendUrl = `${window.location.origin}`;
  const samlLoginUrl = `${process.env.REACT_APP_API_BASE_URL}/api/v1/auth/saml/login/?frontend_url=${frontendUrl}`;
  window.location.href = samlLoginUrl;
};


/**
 * Get the current user's information
 */
export const getCurrentUser = async (): Promise<User> => {
  try {
    const response = await api.get<{ user: User }>('/api/v1/users/me');
    return response.data.user;
  } catch (error) {
    console.error('Failed to get current user information', error);
    const storedUser = getStoredUser();
    if (storedUser) {
      return storedUser;
    }
    throw new Error('Failed to get current user information');
  }
};

/**
 * Log out the current user
 */
export const logout = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('organization');
  localStorage.removeItem('chat_messages');
  localStorage.removeItem('chat_selected_documents');
  localStorage.removeItem('chat_selected_project');
  window.location.href = '/login';
};

/**
 * Log out the current user without redirecting
 */
export const logoutWithoutRedirect = (): void => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  localStorage.removeItem('organization');
  localStorage.removeItem('chat_messages');
  localStorage.removeItem('chat_selected_documents');
  localStorage.removeItem('chat_selected_project');
};

/**
 * Check if the user is authenticated
 */
export const isAuthenticated = (): boolean => {
  return !!localStorage.getItem('token');
};

/**
 * Get the current user from local storage
 */
export const getStoredUser = (): User | null => {
  const userJson = localStorage.getItem('user');
  if (userJson) {
    try {
      return JSON.parse(userJson) as User;
    } catch (error) {
      console.error('Failed to parse user from localStorage', error);
      return null;
    }
  }
  return null;
};

/**
 * Store authentication data in local storage
 */
export const storeAuthData = (user: User): void => {
  localStorage.setItem('user', JSON.stringify(user));
};
